/**
 * Clocking in and out, and when that amounts to attendance.
 *
 * The rule the whole thing turns on:
 *
 *   **Clocking in is always allowed, and a clocked working day counts.**
 *
 * Refusing the button would be the obvious design and the wrong one. Somebody
 * working from a client's office, or from home on a day nobody wrote down, is
 * still working — refusing them loses that fact entirely and tells them nothing
 * they can act on. So the clock-in is recorded and counted, and what varies is
 * only what it says the day *was*:
 *
 *   with a remote arrangement on file  → counts, as whatever that arranged
 *   anything else on a working day     → counts, as office
 *   outside the working pattern,       → recorded, `pending`, counts for
 *   or on a public holiday               nothing until a manager says what it
 *                                        was
 *
 * That last line is the same principle as `rollup.ts`: the system does not
 * decide what it cannot know. A `pending` entry is a question with a timestamp
 * attached, which is strictly more than the passive signals could offer.
 *
 * Where somebody clocked in *from* is deliberately not part of this. Matching
 * the request's address against networks the employer registered is built and
 * dormant — see `presence.ts` — and nothing here consults it: a clock-in from
 * an address nobody registered used to land as a question, which turned every
 * hotspot, VPN and re-leased home connection into a day somebody had to chase.
 * When the networks come back they belong here as *extra* confidence about the
 * status, never as the thing that decides whether the hours count at all.
 */

import type { AttendanceStatus, ClockVerdict, TimeEntryStatus } from "./enums.js";
import type { WorkingPattern } from "./leave.js";
import { isWorkingWeekday } from "./leave.js";
import { dayStartMs } from "./timezone.js";

/** Statuses that mean "working, but not from the office". */
export const REMOTE_WORKING_STATUSES: readonly AttendanceStatus[] = [
  "wfh",
  "client_site",
  "travel",
  "training",
];

/** `HH:MM` to minutes past midnight. */
export function minutesOfDay(time: string): number {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!match) throw new Error(`Not a time of day: ${time}`);

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) throw new Error(`Not a time of day: ${time}`);
  return hours * 60 + minutes;
}

/** The shape of one person's working day. */
export interface WorkDayWindow {
  start: string;
  midday: string;
  end: string;
}

export function validateWorkDay(window: WorkDayWindow): void {
  const start = minutesOfDay(window.start);
  const midday = minutesOfDay(window.midday);
  const end = minutesOfDay(window.end);

  if (!(start < midday && midday < end)) {
    throw new Error(
      `A working day must run start → midday → end; got ${window.start}, ${window.midday}, ${window.end}`,
    );
  }
}

/**
 * How much of a half a clock-in has to cover for that half to count as worked.
 *
 * Half of it. Definitional rather than policy: the unit being decided *is* the
 * half-day, so "more of it than not" is the only threshold that does not need
 * an argument — and anything else would be a number somebody has to defend.
 *
 * An employee who works part of a half and nothing else does not lose the time;
 * the half simply goes unconfirmed, and they or a manager say what the rest of
 * it was.
 */
export const HALF_COVERAGE = 0.5;

export interface ClockedPeriod {
  started_at: string;
  ended_at: string | null;
}

/** Minutes of overlap between a clocked period and a window on its own day. */
function overlapMinutes(
  period: ClockedPeriod,
  date: string,
  fromMinutes: number,
  toMinutes: number,
  now: Date,
): number {
  // Midnight where the business is. A working day written `09:00`-`17:00` is
  // nine to five on that employee's clock, so the window has to be laid over
  // their midnight rather than Greenwich's -- four hours out, which is enough
  // to put a whole morning outside the half it covers.
  const dayStart = dayStartMs(date);
  if (Number.isNaN(dayStart)) return 0;

  const windowFrom = dayStart + fromMinutes * 60_000;
  const windowTo = dayStart + toMinutes * 60_000;

  const started = Date.parse(period.started_at);
  // An entry still open counts up to now, so today's grid reflects the morning
  // somebody is in the middle of rather than waiting for them to clock out.
  const ended = period.ended_at ? Date.parse(period.ended_at) : now.getTime();
  if (Number.isNaN(started) || Number.isNaN(ended)) return 0;

  const from = Math.max(started, windowFrom);
  const to = Math.min(ended, windowTo);
  return Math.max(0, (to - from) / 60_000);
}

export interface WorkedHalves {
  am: boolean;
  pm: boolean;
  am_minutes: number;
  pm_minutes: number;
  total_minutes: number;
}

/**
 * Which halves of a day a set of clocked periods actually covers.
 *
 * Several periods add up — a morning, a lunch break, an afternoon is one day,
 * not two half days — so coverage is summed across them before the threshold is
 * applied.
 */
export function workedHalves(
  periods: readonly ClockedPeriod[],
  date: string,
  window: WorkDayWindow,
  now: Date = new Date(),
): WorkedHalves {
  validateWorkDay(window);

  const start = minutesOfDay(window.start);
  const midday = minutesOfDay(window.midday);
  const end = minutesOfDay(window.end);

  let amMinutes = 0;
  let pmMinutes = 0;
  let total = 0;

  for (const period of periods) {
    amMinutes += overlapMinutes(period, date, start, midday, now);
    pmMinutes += overlapMinutes(period, date, midday, end, now);

    const started = Date.parse(period.started_at);
    const ended = period.ended_at ? Date.parse(period.ended_at) : now.getTime();
    if (!Number.isNaN(started) && !Number.isNaN(ended)) {
      total += Math.max(0, (ended - started) / 60_000);
    }
  }

  return {
    am: amMinutes >= (midday - start) * HALF_COVERAGE,
    pm: pmMinutes >= (end - midday) * HALF_COVERAGE,
    am_minutes: Math.round(amMinutes),
    pm_minutes: Math.round(pmMinutes),
    total_minutes: Math.round(total),
  };
}

/** What the employer has already agreed about this person on this date. */
export interface ClockContext {
  date: string;
  pattern: WorkingPattern;
  /** The standing arrangement for this weekday, if any. */
  standingStatus: AttendanceStatus | null;
  /**
   * A remote-working status a person already put on the grid for this date — a
   * manager settling the day in advance. An arrangement, so a clock-in from
   * outside counts.
   */
  recordedStatus: AttendanceStatus | null;
  /**
   * The status an approved work-from-home request covers this date with, or null
   * if none does.
   *
   * Deliberately *not* read off the attendance grid. Approving a request writes
   * nothing onto the grid, because a day somebody was permitted to work from
   * home is not a day they worked — materialising it at approval time would pay
   * an approved Monday that nobody turned up for. The approval only changes what
   * a clock-in from home *means*; the day still has to be worked to count.
   */
  approvedWfhStatus: AttendanceStatus | null;
  /** Whether the date is a public holiday for this employee's territory. */
  isHoliday: boolean;
}

export interface ClockJudgement {
  status: TimeEntryStatus;
  verdict: ClockVerdict;
  /** The attendance status this clock-in argues for, when it counts. */
  proposes: AttendanceStatus | null;
  /** Said back to whoever clocked in, so a `pending` entry explains itself. */
  message: string;
}

/**
 * The remote-working status already arranged for this date, if any.
 *
 * Three things count as an arrangement, and all three are decisions somebody
 * made rather than inferences: an approved work-from-home request, a standing
 * weekday pattern, and a status already on the grid for that date. The order is
 * the order of specificity — a request names this one date, the grid names it
 * too but may be a manager settling it in advance, and the pattern names every
 * such weekday.
 *
 * Returning the status rather than a boolean is what lets the day read as what
 * was arranged — a Wednesday at a client's site is `client_site`, not `wfh`
 * because that was the nearest word to hand.
 */
export function arrangedRemoteStatus(context: ClockContext): AttendanceStatus | null {
  if (context.approvedWfhStatus) return context.approvedWfhStatus;
  if (context.recordedStatus && REMOTE_WORKING_STATUSES.includes(context.recordedStatus)) {
    return context.recordedStatus;
  }
  if (context.standingStatus && REMOTE_WORKING_STATUSES.includes(context.standingStatus)) {
    return context.standingStatus;
  }
  return null;
}

/** Whether working away from the office was already arranged for this date. */
export function remoteArranged(context: ClockContext): boolean {
  return arrangedRemoteStatus(context) !== null;
}

/**
 * Judges one clock-in.
 *
 * Never refuses, and on a day somebody was due to work never withholds the
 * hours either. The only `pending` left is the day that was not a working day
 * at all, which is a question about what the time *was* rather than a doubt
 * about whether it happened.
 */
export function judgeClockIn(context: ClockContext): ClockJudgement {
  /*
   * A clock-in on a day nobody was due to work is recorded and counts for
   * nothing by itself. It is not wrong — people do catch up at weekends — but
   * turning it into a working day would quietly create time off in lieu that
   * nobody agreed to, so it waits for somebody to say what it was.
   */
  if (context.isHoliday || !isWorkingWeekday(context.date, context.pattern)) {
    return {
      status: "pending",
      verdict: "no_working_day",
      proposes: null,
      message: context.isHoliday
        ? "Clocked in on a public holiday. It is recorded, but will not count as a working day " +
          "unless a manager says otherwise."
        : "Clocked in outside your working pattern. It is recorded, but will not count as a " +
          "working day unless a manager says otherwise.",
    };
  }

  const arranged = arrangedRemoteStatus(context);
  if (arranged) {
    return {
      status: "counted",
      verdict: "work_from_home_approved",
      proposes: arranged,
      message: context.approvedWfhStatus
        ? "Clocked in, with work from home approved for today."
        : "Clocked in away from the office, which is already arranged for today.",
    };
  }

  /*
   * Nothing arranged, so the day reads as the ordinary one: at work. The
   * employee can still say otherwise on the grid, and a manager can correct it
   * — which is a better first guess than a question nobody asked for.
   */
  return {
    status: "counted",
    verdict: "clocked_in",
    proposes: "office",
    message: "Clocked in.",
  };
}

/**
 * How long an open entry may run before it is closed on the employee's behalf.
 *
 * Somebody who forgets to clock out should not read as having worked thirty
 * hours. The entry is closed at the end of their working day and flagged
 * `auto_closed`, so the day still counts and the record says plainly that the
 * end time was supplied rather than observed.
 */
export const MAX_OPEN_HOURS = 16;

/** The timestamp an entry left open overnight should have ended at. */
export function autoCloseAt(startedAt: string, date: string, window: WorkDayWindow): string {
  const dayEnd = dayStartMs(date) + minutesOfDay(window.end) * 60_000;
  const started = Date.parse(startedAt);

  // Somebody who clocked in after their own day was due to end is closed a
  // notional hour later rather than before they began.
  return new Date(Math.max(dayEnd, started + 3_600_000)).toISOString();
}

// ---- What a day of clocking adds up to -------------------------------------

/** Whether an entry's time counts towards the day it was clocked on. */
export function entryCounts(entry: { status: TimeEntryStatus }): boolean {
  return entry.status === "counted" || entry.status === "open";
}

/** One clocked entry, as the arithmetic needs it. */
export interface ClockedEntry extends ClockedPeriod {
  status: TimeEntryStatus;
}

/** Minutes an entry spans — up to `now` while it is still open. */
export function elapsedMinutes(period: ClockedPeriod, now: Date = new Date()): number {
  const started = Date.parse(period.started_at);
  const ended = period.ended_at ? Date.parse(period.ended_at) : now.getTime();
  if (Number.isNaN(started) || Number.isNaN(ended)) return 0;
  return Math.max(0, Math.round((ended - started) / 60_000));
}

/**
 * One person's one day of clocking, totalled.
 *
 * Several entries add up: a morning, a lunch break, an afternoon, a late hour
 * after dinner is one day's effort and not four. `first_in` and `last_out` are
 * the bracket the day actually ran between, which is a different number from
 * the total — somebody who clocked in at nine, out at twelve, in at four and out
 * at six worked five hours across a nine-hour span, and both facts belong on a
 * timesheet.
 *
 * `counted_minutes` and `uncounted_minutes` are kept apart rather than summed
 * because the difference is the whole point: time clocked outside somebody's
 * working pattern is recorded in full and counts for nothing until a manager
 * says what it was. Showing one number would hide exactly the hours somebody
 * needs to ask about.
 */
export interface DayEffort {
  date: string;
  /** Minutes that count: clocked on a day this person was due to work. */
  counted_minutes: number;
  /** Minutes clocked that count for nothing until somebody says what they were. */
  uncounted_minutes: number;
  /** The first clock-in and last clock-out of the day, counted or not. */
  first_in: string | null;
  last_out: string | null;
  /** Minutes between the two, which is not the same as the time worked. */
  span_minutes: number;
  /** Still clocked in, so the totals are as at the time of asking. */
  open: boolean;
  entries: number;
  /** Entries waiting on somebody to say where the person was. */
  pending: number;
  /** Which halves the counted time covers, against this person's working day. */
  worked: WorkedHalves;
}

export function dayEffort(
  entries: readonly ClockedEntry[],
  date: string,
  window: WorkDayWindow,
  now: Date = new Date(),
): DayEffort {
  const counted = entries.filter(entryCounts);
  const worked = workedHalves(counted, date, window, now);

  let uncounted = 0;
  let pending = 0;
  for (const entry of entries) {
    if (entryCounts(entry)) continue;
    uncounted += elapsedMinutes(entry, now);
    if (entry.status === "pending") pending += 1;
  }

  const starts = entries.map((e) => e.started_at).filter(Boolean);
  const open = entries.some((e) => !e.ended_at);
  const ends = entries.map((e) => e.ended_at).filter((e): e is string => !!e);

  const firstIn = starts.length > 0 ? starts.reduce((a, b) => (a < b ? a : b)) : null;
  const lastOut = open || ends.length === 0 ? null : ends.reduce((a, b) => (a > b ? a : b));

  return {
    date,
    counted_minutes: worked.total_minutes,
    uncounted_minutes: Math.round(uncounted),
    first_in: firstIn,
    last_out: lastOut,
    span_minutes: firstIn ? elapsedMinutes({ started_at: firstIn, ended_at: lastOut }, now) : 0,
    open,
    entries: entries.length,
    pending,
    worked,
  };
}

/** A month of days, totalled — what the bottom of a timesheet says. */
export interface EffortTotals {
  counted_minutes: number;
  uncounted_minutes: number;
  /** Days with any counted time at all. */
  days_clocked: number;
  /** Days with something still waiting on an answer. */
  days_pending: number;
  entries: number;
}

export function sumEffort(days: readonly DayEffort[]): EffortTotals {
  return days.reduce<EffortTotals>(
    (total, day) => ({
      counted_minutes: total.counted_minutes + day.counted_minutes,
      uncounted_minutes: total.uncounted_minutes + day.uncounted_minutes,
      days_clocked: total.days_clocked + (day.counted_minutes > 0 ? 1 : 0),
      days_pending: total.days_pending + (day.pending > 0 ? 1 : 0),
      entries: total.entries + day.entries,
    }),
    {
      counted_minutes: 0,
      uncounted_minutes: 0,
      days_clocked: 0,
      days_pending: 0,
      entries: 0,
    },
  );
}

/**
 * The minutes a working day is contracted to run.
 *
 * Start to end, lunch included — the same bracket the halves are measured
 * against, so "seven and a half hours against eight" compares two numbers that
 * were worked out the same way. A timesheet that compared worked time to a
 * figure from somewhere else would invite an argument nobody can settle.
 */
export function contractedMinutes(window: WorkDayWindow): number {
  validateWorkDay(window);
  return minutesOfDay(window.end) - minutesOfDay(window.start);
}
